import React from "react";
import { MapPin, Phone, BadgeCheck, Clock, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { StarRating } from "@/components/StarRating";
import { CATEGORY_META, TIER_STYLE } from "@/lib/constants";

export const ProviderCard = ({ provider, onCall, onRequest, onOpen, index = 0 }) => {
  const meta = CATEGORY_META[provider.category] || CATEGORY_META.mechanics;
  const tier = provider.tier || "free";
  const { Icon } = meta;
  return (
    <div
      data-testid={`provider-card-${provider.id}`}
      className="group relative bg-slate-900/70 backdrop-blur-sm border border-slate-800 hover:border-amber-500/50 transition-all duration-300 rounded-2xl overflow-hidden shadow-lg animate-fade-up flex flex-col"
      style={{ animationDelay: `${Math.min(index * 60, 400)}ms` }}
    >
      {tier === "gold" && (
        <div className="absolute top-0 right-0 h-16 w-16 overflow-hidden pointer-events-none">
          <div className="absolute rotate-45 bg-gradient-to-r from-amber-400 to-yellow-600 text-slate-950 text-[9px] font-black py-0.5 w-24 text-center top-3 -right-6 uppercase tracking-wider">Featured</div>
        </div>
      )}
      {provider.image_url ? (
        <div className="h-32 w-full overflow-hidden cursor-pointer" onClick={() => onOpen?.(provider)}>
          <img src={provider.image_url} alt={provider.business_name}
            className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
        </div>
      ) : (
        <div className="h-32 w-full flex items-center justify-center bg-gradient-to-br from-slate-800 to-slate-900 cursor-pointer" onClick={() => onOpen?.(provider)}>
          <Icon size={40} className="text-slate-700" />
        </div>
      )}

      <div className="p-5 flex flex-col gap-3 flex-1">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 onClick={() => onOpen?.(provider)}
              className="font-head font-bold text-lg leading-tight text-slate-50 truncate cursor-pointer hover:text-amber-400 transition-colors flex items-center gap-1.5">
              {provider.business_name}
              {provider.verified && <BadgeCheck size={16} className="text-cobalt shrink-0 text-blue-400" />}
            </h3>
            <div className="flex items-center gap-1 text-xs text-slate-400 mt-0.5">
              <Icon size={12} className="text-amber-500" />
              <span>{meta.short}</span>
            </div>
          </div>
          <span className={`shrink-0 text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider ${TIER_STYLE[tier]}`}>
            {tier}
          </span>
        </div>

        <div className="flex items-center gap-3 text-xs text-slate-400">
          <div className="flex items-center gap-1">
            <StarRating value={provider.rating} size={12} />
            <span className="text-slate-300 font-semibold">{provider.rating || "New"}</span>
            {provider.review_count > 0 && <span>({provider.review_count})</span>}
          </div>
          <span className="flex items-center gap-1"><Clock size={12} /> ~{provider.response_time_mins}m</span>
        </div>

        <div className="flex items-center gap-1 text-xs text-slate-400">
          <MapPin size={12} className="text-amber-500" /> {provider.city}
        </div>

        {provider.services?.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {provider.services.slice(0, 3).map((s) => (
              <span key={s} className="text-[10px] bg-slate-800 text-slate-300 px-2 py-0.5 rounded-full">{s}</span>
            ))}
          </div>
        )}

        <div className="flex gap-2 mt-auto pt-2">
          <Button
            data-testid={`provider-call-now-button-${provider.id}`}
            onClick={() => onCall?.(provider)}
            variant="outline"
            className="flex-1 border-slate-700 bg-slate-800/50 hover:bg-slate-700 text-slate-100 rounded-xl h-10"
          >
            <Phone size={15} className="mr-1.5" /> Call
          </Button>
          <Button
            data-testid={`provider-request-dispatch-button-${provider.id}`}
            onClick={() => onRequest?.(provider)}
            className="flex-1 bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold rounded-xl h-10"
          >
            <Zap size={15} className="mr-1.5" /> Call-out
          </Button>
        </div>
      </div>
    </div>
  );
};
